import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ReportPhotoDTO } from '../types';
import PhotoItem from './PhotoItem';
import { Box } from '@mui/material';
import { DragIndicator as DragIcon } from '@mui/icons-material';

interface DraggablePhotoItemProps<TPhoto extends ReportPhotoDTO = ReportPhotoDTO> {
  photo: TPhoto;
  index: number;
  movePhoto: (dragIndex: number, hoverIndex: number) => void;
  onUpdateCaption: (photoId: string, caption: string) => void;
  onDelete: (photoId: string) => Promise<void>;
  onEditPhoto?: (photoId: string, editedBlob: Blob, editedUrl: string) => Promise<void>;
  onRotate?: (photoId: string) => void;
  onSaveRotation?: (photoId: string) => Promise<void>;
  pendingRotation?: number;
  isSavingRotation?: boolean;
  thumbnailUrl: string;
  localCaption: string;
  onCaptionChange: (photoId: string, caption: string) => void;
  onMoveToNext?: (currentIndex: number) => void;
  onMoveToPrevious?: (currentIndex: number) => void;
  onMoveToBottom?: (photoId: string) => void;
  onRegisterEditTrigger?: (triggerEdit: () => void) => void;
  getImageAsBase64: (blobName: string) => Promise<string>;
  isReadOnly?: boolean;
  maxCaptionLength?: number;
}

const DRAG_TYPE = 'PHOTO_ITEM';

interface DragItem {
  photoId: string;
  index: number;
  isUploading?: boolean;
}

const DraggablePhotoItem = <TPhoto extends ReportPhotoDTO = ReportPhotoDTO>({
  photo,
  index,
  movePhoto,
  onUpdateCaption,
  onDelete,
  onEditPhoto,
  onRotate,
  onSaveRotation,
  pendingRotation,
  isSavingRotation,
  thumbnailUrl,
  localCaption,
  onCaptionChange,
  onMoveToNext,
  onMoveToPrevious,
  onMoveToBottom,
  onRegisterEditTrigger,
  getImageAsBase64,
  isReadOnly = false,
  maxCaptionLength,
}: DraggablePhotoItemProps<TPhoto>) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const [{ isDragging }, dragRef] = useDrag({
    type: DRAG_TYPE,
    item: { photoId: photo.id, index, isUploading: false },
    canDrag: !isReadOnly,
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, dropRef] = useDrop({
    accept: DRAG_TYPE,
    canDrop: () => !isReadOnly,
    hover: (item: DragItem, monitor) => {
      if (!ref.current || item.isUploading || isReadOnly) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      movePhoto(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  // Combine drag and drop refs
  dragRef(dropRef(ref));

  const handleMoveToNext = () => {
    if (onMoveToNext) {
      onMoveToNext(index);
    }
  };

  const handleMoveToPrevious = () => {
    if (onMoveToPrevious) {
      onMoveToPrevious(index);
    }
  };

  const handleRegisterEditTrigger = (triggerEdit: () => void) => {
    if (onRegisterEditTrigger) {
      onRegisterEditTrigger(triggerEdit);
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        ...(!isReadOnly && styles.container),
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Box sx={styles.wrapper}>
        {/* Drag Handle */}
        {!isReadOnly && (
          <Box sx={styles.dragHandle} className='photo-drag-indicator'>
            <DragIcon fontSize='small' />
          </Box>
        )}

        <PhotoItem
          photo={photo}
          onUpdateCaption={onUpdateCaption}
          onDelete={onDelete}
          onEditPhoto={onEditPhoto}
          onRotate={onRotate ? () => onRotate(photo.id) : undefined}
          onSaveRotation={onSaveRotation ? () => onSaveRotation(photo.id) : undefined}
          pendingRotation={pendingRotation}
          isSavingRotation={isSavingRotation}
          thumbnailUrl={thumbnailUrl}
          localCaption={localCaption}
          onCaptionChange={onCaptionChange}
          onMoveToNext={handleMoveToNext}
          onMoveToPrevious={handleMoveToPrevious}
          onMoveToBottom={onMoveToBottom ? () => onMoveToBottom(photo.id) : undefined}
          onRegisterEditTrigger={handleRegisterEditTrigger}
          getImageAsBase64={getImageAsBase64}
          isReadOnly={isReadOnly}
          maxCaptionLength={maxCaptionLength}
        />
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  wrapper: {
    position: 'relative' as const,
  },
  dragHandle: {
    position: 'absolute' as const,
    top: 8,
    left: 8,
    zIndex: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },
};

export default DraggablePhotoItem;
